import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, RotateCcw, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { DocumentExportActions } from './DocumentExportActions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const ReturnInwardChallanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/return-inward-challans/${id}`, getAuthHeaders());
        setDoc(res.data);
      } catch {
        setError('Failed to load Return Inward Challan');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  if (error) return (
    <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm">
      <AlertCircle className="h-5 w-5" />{error}
    </div>
  );
  if (!doc) return null;

  const units = doc.returned_units || [];

  // Group units by product for summary
  const byProduct = {};
  units.forEach(u => {
    const key = u.product_name || u.product_id || 'Unknown';
    if (!byProduct[key]) byProduct[key] = 0;
    byProduct[key]++;
  });

  const exportConfig = {
    title: 'Return Inward DC', number: doc.return_number,
    metadata: [
      { label: 'Return Number', value: doc.return_number || '-' }, { label: 'Return Date', value: formatDate(doc.return_date) },
      { label: 'Source IDC', value: doc.source_idc_number || '-' }, { label: 'Vendor', value: doc.vendor_name || '-' },
      { label: 'Company', value: doc.company_name || '-' }, { label: 'Status', value: doc.status || '-' },
    ],
    columns: [
      { label: '#', value: (_, index) => index + 1, type: 'number' }, { label: 'Product', value: unit => unit.product_name || '-' },
      { label: 'Unit #', value: unit => Number(unit.unit_number || 0), type: 'number' }, { label: 'Item ID', value: unit => unit.item_id || '-' },
      { label: 'Remark / Reason', value: unit => unit.remark || '-' },
    ],
    rows: units,
    totals: [{ label: 'Total Units Returned', value: String(units.length), rawValue: units.length }],
    shareLines: [`Vendor: ${doc.vendor_name || '-'}`, `Return Date: ${formatDate(doc.return_date)}`, `Total Units Returned: ${units.length}`, `Status: ${doc.status || '-'}`],
  };

  return (
    <div data-testid="return-inward-challan-detail-page">
      <button
        onClick={() => navigate('/admin/purchase/return-inward-challan')}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors print:hidden"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Return Inward DCs
      </button>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Return Inward DC</h1>
          <span className="px-3 py-1 text-xs font-bold uppercase rounded bg-red-100 text-red-700">
            {doc.status}
          </span>
        </div>
        {doc.source_idc_id && (
          <button
            onClick={() => navigate(`/admin/purchase/inward-challan/${doc.source_idc_id}`)}
            data-testid="view-source-idc-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 border border-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-slate-50 transition-colors print:hidden"
          >
            <ExternalLink className="h-3.5 w-3.5" /> View Source IDC
          </button>
        )}
      </div>

      <div className="mb-8"><DocumentExportActions config={exportConfig} testIdPrefix="return-inward-dc" /></div>

      {/* Return Info */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[
            { label: 'Return Number', value: doc.return_number },
            { label: 'Return Date', value: formatDate(doc.return_date) },
            { label: 'Source IDC', value: doc.source_idc_number || '-' },
            { label: 'Vendor', value: doc.vendor_name || '-' },
            { label: 'Company', value: doc.company_name || '-' },
            { label: 'Total Units Returned', value: units.length },
          ].map(({ label, value }) => (
            <div key={label}>
              <span className="block text-xs font-medium text-slate-500 uppercase">{label}</span>
              <span className="text-sm font-semibold text-slate-900">{value}</span>
            </div>
          ))}
          {doc.notes && (
            <div className="col-span-2">
              <span className="block text-xs font-medium text-slate-500 uppercase">Notes</span>
              <span className="text-sm text-slate-900">{doc.notes}</span>
            </div>
          )}
        </div>
      </div>

      {/* Product Summary */}
      {Object.keys(byProduct).length > 0 && (
        <div className="bg-white rounded-sm border border-slate-200 mb-6">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Product Summary</h2>
          </div>
          <div className="p-6 flex flex-wrap gap-3">
            {Object.entries(byProduct).map(([name, count]) => (
              <div key={name} className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-sm">
                <span className="text-sm font-medium text-slate-800">{name}</span>
                <span className="text-xs font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">{count} unit{count > 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Returned Units Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Returned Units</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase">
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-center">Unit #</th>
                <th className="px-4 py-3 text-left">Item ID</th>
                <th className="px-4 py-3 text-left">Remark / Reason</th>
              </tr>
            </thead>
            <tbody>
              {units.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">No units recorded</td>
                </tr>
              ) : (
                units.map((u, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{u.product_name || '-'}</td>
                    <td className="px-4 py-3 text-center text-slate-700">{u.unit_number}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{u.item_id || '-'}</td>
                    <td className="px-4 py-3 text-slate-600 italic">
                      {u.remark ? (
                        <span className="text-red-700 not-italic bg-red-50 px-2 py-0.5 rounded text-xs">{u.remark}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
